import { get, put } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";

export interface TierRuleCondition {
  field_name: string;
  operator: string;
  values: string[];
}

export interface TierRule {
  id: string;
  rule_name: string;
  tier_number: number;
  conditions: TierRuleCondition[];
}

export interface TierMappingControlSet {
  id: string;
  name: string;
  number_of_controls: number;
}

export interface TierMappingQuestionnaire {
  id: string;
  name: string;
  number_of_questions: number;
  number_of_evidences: number;
}

export interface TierMapping {
  id: string;
  organization_id: string;
  tier_number: number;
  tier_name: string;
  tier_description: string;
  control_sets: TierMappingControlSet[];
  questionnaires: TierMappingQuestionnaire[];
  organization_available_control_sets: TierMappingControlSet[];
  organization_available_questionnaires: TierMappingQuestionnaire[];
  tier_rules: TierRule[];
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface GetTierMappingsResponse {
  success: boolean;
  tier_mappings: TierMapping[];
  message?: string;
}

export const getTierMappings = async (): Promise<GetTierMappingsResponse> => {
  return get<GetTierMappingsResponse>({
    url: APIEndpoints.TierMappings,
    isAuthRequired: true,
  });
};

export interface UpdateTierMappingPayload {
  control_set_ids: string[];
  questionnaire_ids: string[];
}

export const updateTierMapping = async (
  tierMappingId: string,
  payload: UpdateTierMappingPayload,
): Promise<TierMapping> => {
  return put<UpdateTierMappingPayload, TierMapping>({
    url: `${APIEndpoints.TierMappings}/${tierMappingId}`,
    data: payload,
    isAuthRequired: true,
  });
};

export const getTierMappingsList = async (): Promise<TierMapping[]> => {
  const response = await getTierMappings();

  if (!response.success) {
    throw new Error(response.message || "Failed to fetch tier mappings.");
  }

  if (!Array.isArray(response.tier_mappings)) {
    throw new Error("Invalid tier mappings response.");
  }

  return response.tier_mappings;
};
